import {
  ClipboardCopyIcon,
  DownloadIcon,
  PersonIcon,
  PlayIcon,
} from '@radix-ui/react-icons';
import { Box, Button, Flex, IconButton, Switch, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { FileHelper } from 'classes/helpers/file.helper';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  DetectionFlag,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { getHitterOption } from 'components/common/hitters/hitter-option';
import { CommonTable } from 'components/common/table';
import { TableContext, TableProvider } from 'components/common/table/context';
import { getFireVideoID } from 'components/common/video/annotated-player';
import { OutcomeOverrideSelector } from 'components/sections/analytics/outcome-override-selector';
import { DetectionIssueDialog } from 'components/sections/analytics/session-hits/detection-issue-dialog';
import { SessionHitsToolbar } from 'components/sections/analytics/session-hits/toolbar';
import { SessionHitsVideoDialog } from 'components/sections/analytics/session-hits/video-dialog';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { useSessionRecapHits } from 'components/sections/analytics/store/use-session-recap-hits';
import { CheckedProvider } from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { SESSION_HIT_RESULTS } from 'enums/session-recap/enums';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import useSyncRecapLive from 'hooks/pitch-list/useSyncRecapLive';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IZipFile } from 'interfaces/forms/file';
import { IVerboseComponent } from 'interfaces/i-components';
import { UnitFactors } from 'interfaces/i-machine-context';
import { IMenuAction } from 'interfaces/i-menus';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { isInRange, round } from 'lib_ts/classes/math.utilities';
import { PlateHelper, VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_DATETIME_FORMAT_FILENAME,
  LOCAL_PRETTY_DATETIME_PRECISE_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import {
  HitterDataSource,
  MAP_OUTCOME_TO_PRETTY_LABEL,
  OutcomeCode,
  getHideHitDetectionsByOutcome,
} from 'lib_ts/enums/hitters.enums';
import { StrikeZoneType } from 'lib_ts/enums/machine.enums';
import { tooltipPitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IExpiringUrl, IUrlRequest } from 'lib_ts/interfaces/common/i-url-dict';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import {
  IDetectionIssue,
  ISessionHit,
  IUpdateHitRequest,
} from 'lib_ts/interfaces/session-hits/i-session-hit';
import { compact, first, isNil, merge } from 'lodash';
import {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { HittersService } from 'services/hitters.service';
import { MainService } from 'services/main.service';
import { StereoVideosService } from 'services/stereo-videos.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';
import useHittersStore from 'stores/use-hitters-store';
import useMiscStore from 'stores/use-misc-store';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

const MIN_CONFIRM_COUNT = 150;

const VALUE_FALLBACK = '-';

const roundToZero = (v?: number | null) => (v?.toFixed(0) ? round(v, 0) : null);
const roundToOne = (v?: number | null | undefined) => {
  if (isNil(v)) {
    return null;
  }

  if (isNaN(v)) {
    return null;
  }

  return round(v, 1).toString();
};

const getHitSortValue = ({
  a,
  b,
  key,
  dir,
}: {
  a: ISessionHit;
  b: ISessionHit;
  key: keyof ISHHitDetails;
  dir: number;
}) => {
  // Infinity and -Infinity ensures invalid hits are grouped together at the top or bottom
  if (dir === -1) {
    const aHitDistance = a.hit?.invalid
      ? -Infinity
      : ((a.hit?.[key] ?? 0) as number);

    const bHitDistance = b.hit?.invalid
      ? -Infinity
      : ((b.hit?.[key] ?? 0) as number);

    return (aHitDistance - bHitDistance) * dir;
  }

  const aHitDistance = a.hit?.invalid
    ? -Infinity
    : ((a.hit?.[key] ?? -Infinity) as number);

  const bHitDistance = b.hit?.invalid
    ? -Infinity
    : ((b.hit?.[key] ?? -Infinity) as number);

  return (aHitDistance - bHitDistance) * dir;
};

interface IGenerateHitterSessionColumnsParams {
  szDef: IStrikeZone;
  isAdmin: boolean;
  unitFactors: UnitFactors;
  isMetric?: boolean;
  byID: Record<string, ISessionRecapHit>;
  onUpdate: (id: string, value: IUpdateHitRequest) => void;
  onPlay: (hit: ISessionHit) => void;
  openDetectionIssueDialog: (hit: ISessionHit) => void;
  onReassign: (hit: ISessionHit) => void;
}

const resultSortIndex = (r: ISessionRecapHit | undefined) =>
  r?.result
    ? SESSION_HIT_RESULTS.indexOf(r.result)
    : SESSION_HIT_RESULTS.length;

const generateHitterSessionColumns = ({
  szDef,
  isAdmin,
  unitFactors,
  isMetric,
  byID,
  onUpdate,
  onPlay,
  openDetectionIssueDialog,
  onReassign,
}: IGenerateHitterSessionColumnsParams): ITypedTableColumn<ISessionHit>[] => [
  {
    label: 'common.created',
    key: '_created',
    dataType: 'datetime',
    invisible: true,
  },
  {
    label: '#',
    key: 'pitch-number',
    align: 'right',
    formatFn: (m) => byID[m._id]?.pitchNumber ?? VALUE_FALLBACK,
    sortRowsFn: (a, b, dir: number) =>
      ((byID[a._id]?.pitchNumber ?? 0) - (byID[b._id]?.pitchNumber ?? 0)) * dir,
  },
  {
    label: 'common.pitch',
    key: 'pitch',
    formatFn: (m) => (
      <Flex direction="column" gap="1" mt="2" mb="2">
        <Text truncate>{m.pitch?.name || VALUE_FALLBACK}</Text>
        <Text color={RADIX.COLOR.NEUTRAL} size="1">
          {format(parseISO(m._created), LOCAL_PRETTY_DATETIME_PRECISE_FORMAT, {
            timeZone: LOCAL_TIMEZONE,
          })}
        </Text>
      </Flex>
    ),
    sortRowsFn: (a, b, dir: number) =>
      StringHelper.safeSort(a._created, b._created) * dir,
  },
  {
    label: '',
    key: 'hit.video-playback',
    align: 'center',
    formatFn: (hit) => {
      const videoID = getFireVideoID({
        annotated_video_id: hit.fire.annotated_video_id,
        replay_video_id: hit.fire.replay_video_id,
      });

      return (
        <IconButton
          variant="soft"
          size="1"
          disabled={!videoID}
          onClick={(e) => {
            e.stopPropagation();
            onPlay(hit);
          }}
        >
          <PlayIcon />
        </IconButton>
      );
    },
    disableSort: true,
  },
  {
    label: 'common.type',
    key: 'pitch.type',
    formatFn: ({ pitch }) => pitch?.type,
    tooltipFn: ({ pitch }) => t(tooltipPitchType(pitch?.type)).toString(),
    sortRowsFn: (a, b, dir: number) =>
      -StringHelper.safeSort(a.pitch?.type, b.pitch?.type) * dir,
  },
  {
    label: 'common.speed',
    subLabel: MetricHelper.getSpeedUnit(isMetric),
    key: 'pitch.speedMPH',
    align: 'right',
    formatFn: ({ pitch }) => {
      if (!pitch || isNil(pitch.speedMPH)) {
        return VALUE_FALLBACK;
      }

      return (
        roundToOne(pitch.speedMPH * unitFactors.fromSpeedMph) ?? VALUE_FALLBACK
      );
    },
    sortRowsFn: (a, b, dir: number) =>
      ((a.pitch?.speedMPH ?? 0) - (b.pitch?.speedMPH ?? 0)) * dir,
  },
  {
    label: 'common.v-break',
    subLabel: MetricHelper.getBreaksUnit(isMetric),
    key: 'pitch.zBreakIN',
    align: 'right',
    formatFn: ({ pitch }) => {
      if (!pitch || isNil(pitch.zBreakIN)) {
        return VALUE_FALLBACK;
      }

      return (
        roundToOne(pitch.zBreakIN * unitFactors.fromBreaksIn) ?? VALUE_FALLBACK
      );
    },
    sortRowsFn: (a, b, dir: number) =>
      ((a.pitch?.zBreakIN ?? 0) - (b.pitch?.zBreakIN ?? 0)) * dir,
  },
  {
    label: 'common.h-break',
    subLabel: MetricHelper.getBreaksUnit(isMetric),
    key: 'pitch.xBreakIN',
    align: 'right',
    formatFn: ({ pitch }) => {
      if (!pitch || isNil(pitch.xBreakIN)) {
        return VALUE_FALLBACK;
      }

      return (
        roundToOne(-1 * pitch.xBreakIN * unitFactors.fromBreaksIn) ??
        VALUE_FALLBACK
      );
    },
    sortRowsFn: (a, b, dir: number) =>
      -1 * (((a.pitch?.xBreakIN ?? 0) - (b.pitch?.xBreakIN ?? 0)) * dir),
  },
  {
    label: 'common.result',
    key: 'hit.outcome',
    formatFn: (h) => {
      if (h.tracking_device === TrackingDevice.TrajektVision) {
        return <OutcomeOverrideSelector hit={h} szDef={szDef} />;
      }

      if (!h.hit?._mx_outcome) {
        return VALUE_FALLBACK;
      }

      const readOnlyOutcome = HitHelper.getOutcome(h, szDef);
      return MAP_OUTCOME_TO_PRETTY_LABEL[
        readOnlyOutcome ?? OutcomeCode.Unknown
      ];
    },
    sortRowsFn: (a, b, dir: number) =>
      (resultSortIndex(byID[a._id]) - resultSortIndex(byID[b._id])) * dir,
  },
  {
    label: 'common.ev',
    subLabel: isMetric ? 'kph' : 'mph',
    key: 'hit.exitMPH',
    align: 'right',
    formatFn: ({ hit }) => {
      if (!hit) {
        return VALUE_FALLBACK;
      }

      if (hit.invalid) {
        // e.g. bad tracking => no exit velocity
        return VALUE_FALLBACK;
      }

      if (hit._mx_outcome && getHideHitDetectionsByOutcome(hit._mx_outcome)) {
        // e.g. ball or called strike => no swing => no exit velocity
        return VALUE_FALLBACK;
      }

      if (isNil(hit.exitMPH) || hit.exitMPH === 0) {
        return VALUE_FALLBACK;
      }

      const value = isMetric ? MetricHelper.mphToKph(hit.exitMPH) : hit.exitMPH;
      return roundToOne(value);
    },
    sortRowsFn: (a, b, dir: number) =>
      getHitSortValue({
        a,
        b,
        key: 'exitMPH',
        dir,
      }),
  },
  {
    label: 'common.la',
    subLabel: 'deg',
    key: 'hit.vLaunchDEG',
    align: 'right',
    formatFn: ({ hit }) => {
      if (!hit) {
        return VALUE_FALLBACK;
      }

      if (hit.invalid) {
        // e.g. bad tracking => no launch angle
        return VALUE_FALLBACK;
      }

      if (hit._mx_outcome && getHideHitDetectionsByOutcome(hit._mx_outcome)) {
        // e.g. ball or called strike => no launch angle
        return VALUE_FALLBACK;
      }

      if (isNil(hit.vLaunchDEG)) {
        return VALUE_FALLBACK;
      }

      return roundToOne(hit?.vLaunchDEG);
    },
    sortRowsFn: (a, b, dir: number) =>
      getHitSortValue({
        a,
        b,
        key: 'vLaunchDEG',
        dir,
      }),
  },
  {
    label: 'common.dist',
    subLabel: MetricHelper.getDistanceUnit(isMetric),
    key: 'hit.distanceFT',
    align: 'right',
    formatFn: ({ hit }) => {
      if (!hit) {
        return VALUE_FALLBACK;
      }

      if (hit.invalid) {
        // e.g. bad tracking => no launch angle
        return VALUE_FALLBACK;
      }

      if (hit._mx_outcome && getHideHitDetectionsByOutcome(hit._mx_outcome)) {
        // e.g. ball or called strike => no exit velocity
        return VALUE_FALLBACK;
      }

      if (isNil(hit.distanceFT)) {
        return VALUE_FALLBACK;
      }

      return roundToZero(hit.distanceFT * unitFactors.fromDistanceFt);
    },
    sortRowsFn: (a, b, dir: number) =>
      getHitSortValue({
        a,
        b,
        key: 'distanceFT',
        dir,
      }),
  },
  {
    label: 'common.location-abbreviation',
    key: 'pitch.plate',
    formatFn: ({ pitch }) => {
      if (!pitch?.plate) {
        return VALUE_FALLBACK;
      }

      const summary = PlateHelper.getPlateSummary(pitch.plate, szDef);

      return (
        <img
          alt="strike zone icon"
          className="StrikeZoneIcon"
          width={24}
          height={24}
          src={useMiscStore
            .getState()
            .getThemeImgPath(`plate/${summary.grid}.svg`)}
        />
      );
    },
    sortRowsFn: (a, b, dir: number) => {
      const [sortA, sortB] = [a, b].map((m) =>
        m.pitch?.plate ? PlateHelper.getPlateSummary(m.pitch.plate).sort : 0
      );

      return -dir * (sortA < sortB ? -1 : 1);
    },
  },
  {
    label: 'hitters.include',
    key: 'hit.avg_excluded',
    align: 'center',
    formatFn: (hit) => (
      <Switch
        title={t(
          hit.avg_excluded
            ? 'hitters.excluded-from-session-stats'
            : 'hitters.included-in-session-stats'
        ).toString()}
        size="1"
        checked={!hit.avg_excluded}
        onCheckedChange={() =>
          onUpdate(hit._id, { avg_excluded: !hit.avg_excluded })
        }
      ></Switch>
    ),
    disableSort: true,
  },
  {
    label: 'Detection Issue',
    labelIcon: <SuperAdminIcon />,
    key: 'hit.detection-flag',
    align: 'center',
    invisible: !isAdmin,
    disableSort: true,
    tooltipFn: (hit) => {
      if (hit.tracking_device === TrackingDevice.TrajektVision) {
        return undefined;
      }

      return `Only relevant for ${TrackingDevice.TrajektVision} tracking`;
    },
    formatFn: (hit) => {
      if (hit.tracking_device !== TrackingDevice.TrajektVision) {
        // show nothing (or dash) for non-Vision tracking
        return null;
      }

      return (
        <IconButton
          variant="soft"
          size="1"
          onClick={() => openDetectionIssueDialog(hit)}
          color={RADIX.COLOR.NEUTRAL}
        >
          <DetectionFlag filled={!!hit?.detectionIssue} />
        </IconButton>
      );
    },
  },
  {
    label: '',
    key: ACTIONS_KEY,
    align: 'left',
    actions: [
      {
        prefixIcon: <ClipboardCopyIcon />,
        label: 'hitters.copy-fire-id',
        onClick: (hit) => {
          navigator.clipboard.writeText(hit.fire._id);

          NotifyHelper.info({
            message_md: t('hitters.fire-id-copied'),
          });
        },
      },
      {
        prefixIcon: <PersonIcon />,
        label: 'hitters.reassign-to-another-hitter',
        onClick: onReassign,
      },
    ],
  },
];

interface IProps extends IVerboseComponent {
  skipHeader?: boolean;
  sessionID: string;
  hitterID: string;
  machineID: string | undefined;
  backToPitches?: () => void;
  /** defaults to true, i.e. the table fills the remaining height */
  vFlex?: boolean;
  /** the table grows with its rows so an ancestor can scroll it */
  disableScrolling?: boolean;
  /** when provided, row clicks and play buttons call this instead of opening the video dialog */
  onOpenHit?: (hit: ISessionHit) => void;
  /** must be referentially stable */
  onHoverHit?: (hit: ISessionHit | undefined) => void;
  /** the filtered hits in the order they're currently displayed */
  onSortedHitsChange?: (hits: ISessionHit[]) => void;
}

const INTERACTIVE_SELECTOR =
  'button, input, a, [role="switch"], [role="checkbox"], [role="menuitem"]';

// reports the table's sorted (as-displayed) order up to the parent so the video
// dialog can step through hits in the same order the user sees them
const SortedHitsProbe = (props: {
  onChange: (hits: ISessionHit[]) => void;
}) => {
  const { sortedData } = useContext(TableContext);
  const { onChange } = props;

  useEffect(() => {
    onChange(sortedData as ISessionHit[]);
  }, [sortedData, onChange]);

  return null;
};

export const SessionHitsTableNew = (props: IProps) => {
  const { sessionID, hitterID, machineID, onOpenHit, onSortedHitsChange } =
    props;

  useSyncRecapLive({
    machineID: machineID ?? null,
    sessionID: sessionID,
    hitterID: hitterID,
  });

  const { unitFactors } = useContext(MachineContext);

  const current = useAuthStore((s) => s.current);

  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);
  const hits = useAnalyticsStore((s) => s.hits);
  const pitchTypeFilter = useAnalyticsStore((s) => s.pitchTypeFilter);
  const outcomeFilter = useAnalyticsStore((s) => s.outcomeFilter);
  const advancedFilters = useAnalyticsStore((s) => s.advancedFilters);
  const newestHitID = useAnalyticsStore((s) => s.newestHitID);
  const updatedHitID = useAnalyticsStore((s) => s.updatedHitID);

  const { byID } = useSessionRecapHits();

  const hitters = useHittersStore((s) => s.hitters);

  const hitterOptions = useMemo(
    () =>
      hitters
        .map((h) =>
          getHitterOption(h, h.source || HitterDataSource.Manual, {
            hidePrefix: true,
          })
        )
        .sort((a, b) => StringHelper.safeSort(a.label, b.label)),
    [hitters]
  );

  const [dialogPlayback, setDialogPlayback] = useState<number>();

  // confirm before downloading a large number of videos
  const [dialogDownload, setDialogDownload] = useState<{
    key: number;
    hits: ISessionHit[];
  }>();

  // select new hitter to reassign hits
  const [dialogReassign, setDialogReassign] = useState<{
    key: number;
    hits: ISessionHit[];
  }>();

  // detection issue form
  const [dialogDetectionIssue, setDialogDetectionIssue] = useState<{
    key: number;
    hitID: string;
    detectionIssue?: Nullable<IDetectionIssue>;
  }>();

  const [reassignHitterID, setReassignHitterID] = useState<string>();

  const szDef = useMemo(() => {
    const hitter = hitters.find((h) => h._id === hitterID);

    if (!hitter || isNil(hitter.height_ft)) {
      return VISIBLE_STRIKEZONE;
    }

    return PlateHelper.getDynamicStrikeZone(
      sessionMachine?.strikeZoneType ?? StrikeZoneType.Default,
      hitter.height_ft
    );
  }, [hitterID, hitters, sessionMachine?.strikeZoneType]);

  const filteredHits = useMemo(
    () =>
      hits.filter((m) => {
        const { pitch, hit } = m;

        if (!pitch?._id) {
          return false;
        }

        if (
          pitchTypeFilter.length > 0 &&
          (!pitch || !pitchTypeFilter.includes(pitch.type))
        ) {
          // Filter by pitch type
          return false;
        }

        const outcome = HitHelper.getOutcome(m, szDef) ?? OutcomeCode.Unknown;

        // Filter by outcome
        if (outcomeFilter.length > 0 && !outcomeFilter.includes(outcome)) {
          return false;
        }

        if (!advancedFilters) {
          return true;
        }

        const {
          speedMPH,
          xBreakIN,
          zBreakIN,
          exitMPH,
          vLaunchDEG,
          distanceFT,
        } = advancedFilters;

        const pitchFilters = [speedMPH, xBreakIN, zBreakIN];
        const hitFilters = [exitMPH, vLaunchDEG, distanceFT];

        const hasPitchFilters = pitchFilters.some((f) => f.value);
        const hasHitFilters = hitFilters.some((f) => f.value);

        // Exclude rows with no pitch data if we're filtering by pitch
        if (hasPitchFilters && !pitch) {
          return false;
        }

        // Exclude rows with no hit data if we're filtering by hit
        if (hasHitFilters && !hit) {
          return false;
        }

        // Filter by advanced filter sliders
        if (!isInRange(speedMPH.value, pitch?.speedMPH)) {
          return false;
        }

        if (
          !isInRange(
            xBreakIN.value,
            pitch?.xBreakIN ? -1 * pitch?.xBreakIN : undefined
          )
        ) {
          return false;
        }

        if (!isInRange(zBreakIN.value, pitch?.zBreakIN)) {
          return false;
        }

        if (!isInRange(exitMPH.value, hit?.exitMPH)) {
          return false;
        }

        if (!isInRange(vLaunchDEG.value, hit?.vLaunchDEG)) {
          return false;
        }

        if (!isInRange(distanceFT.value, hit?.distanceFT)) {
          return false;
        }

        return true;
      }),
    [hits, pitchTypeFilter, outcomeFilter, advancedFilters]
  );

  // the table's data in the order it is currently displayed (respects the
  // user's active column sort), lifted out of the table context via the probe
  const [sortedHits, setSortedHits] = useState<ISessionHit[]>([]);

  const hitsWithVideos = useMemo(
    () =>
      sortedHits.filter((h) =>
        getFireVideoID({
          annotated_video_id: h.fire.annotated_video_id,
          replay_video_id: h.fire.replay_video_id,
        })
      ),
    [sortedHits]
  );

  // defines the starting hit for the dialog, when user clicks to play a specific video
  const [hitPlaybackID, setHitPlaybackID] = useState<string>('');

  const pagination: ITablePageable = {
    total: filteredHits.length,
    enablePagination: true,
    paginationID: PaginationID.HitterSession,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: 1,
    },
  };

  const handleSortedChange = useCallback(
    (hits: ISessionHit[]) => {
      setSortedHits(hits);
      onSortedHitsChange?.(hits);
    },
    [onSortedHitsChange]
  );

  const handleRowClick = useCallback(
    (hit: ISessionHit, event: MouseEvent) => {
      if ((event.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) {
        return;
      }

      onOpenHit?.(hit);
    },
    [onOpenHit]
  );

  const columns = useMemo(
    () =>
      generateHitterSessionColumns({
        szDef: szDef,
        isAdmin: current.role === UserRole.admin,
        isMetric: sessionMachine?.isMetric,
        unitFactors: unitFactors,
        byID: byID,
        onUpdate: (...args) => {
          useAnalyticsStore.setState({
            newestHitID: null,
            updatedHitID: null,
          });
          return useAnalyticsStore.getState().updateHit(...args);
        },
        onPlay: (hit) => {
          if (onOpenHit) {
            onOpenHit(hit);
            return;
          }

          const { fire } = hit;
          const videoID = getFireVideoID({
            annotated_video_id: fire.annotated_video_id,
            replay_video_id: fire.replay_video_id,
          });

          if (!videoID) {
            // machine can support stereo but there isn't a video (yet) associated with this shot
            NotifyHelper.warning({
              message_md: t('common.no-video-playback-msg'),
            });
            return;
          }

          setHitPlaybackID(fire._id);

          // show the dialog
          setDialogPlayback(Date.now());
        },
        openDetectionIssueDialog: (hit) =>
          setDialogDetectionIssue({
            key: Date.now(),
            hitID: hit._id,
            detectionIssue: hit.detectionIssue,
          }),
        onReassign: (hit) =>
          setDialogReassign({
            key: Date.now(),
            hits: [hit],
          }),
      }),
    [
      szDef,
      byID,
      onOpenHit,
      current.role,
      sessionMachine?.isMetric,
      unitFactors,
      setHitPlaybackID,
      setDialogPlayback,
      setDialogDetectionIssue,
    ]
  );

  const handleDownloadVideos = useCallback(
    async (hits: ISessionHit[]) => {
      const urlDict: DictOf<IExpiringUrl> = {};

      const videoIDs = compact(
        ArrayHelper.unique(
          hits.map((h) =>
            getFireVideoID({
              annotated_video_id: h.fire.annotated_video_id,
              replay_video_id: h.fire.replay_video_id,
            })
          )
        )
      );

      if (videoIDs.length === 0) {
        return;
      }

      const videos = await StereoVideosService.getInstance().getMany(videoIDs);

      // get all non-empty video paths
      const paths: IUrlRequest[] = videos.map((h) => ({
        region: h.region,
        path: h.path,
      }));

      // chunk to avoid signing too many URLs at once
      const chunks = ArrayHelper.chunkArray(paths, 20);
      for (const chunk of chunks) {
        merge(urlDict, await MainService.getInstance().signUrls(chunk));
      }

      const firstHit = first(hits);

      if (!firstHit) {
        return;
      }

      const hitter = hitters.find((h) => h._id === hitterID);
      const hitterName =
        hitter?.name ?? firstHit?.hitter?.name ?? 'Anonymous Hitter';

      const files: (IZipFile | undefined)[] = hits.map((hit) => {
        const video = videos.find(
          (m) =>
            m._id ===
            getFireVideoID({
              annotated_video_id: hit.fire.annotated_video_id,
              replay_video_id: hit.fire.replay_video_id,
            })
        );

        const entry = urlDict[video?.path ?? ''];

        if (!entry) {
          verboseDebug(
            props,
            'download video > skipped hit > no signed url',
            getFireVideoID({
              annotated_video_id: hit.fire.annotated_video_id,
              replay_video_id: hit.fire.replay_video_id,
            }),
            urlDict
          );
          return;
        }

        const name = [
          format(parseISO(hit._created), LOCAL_DATETIME_FORMAT_FILENAME, {
            timeZone: LOCAL_TIMEZONE,
          }),
          slugify(hitterName, {
            replacement: '_',
            strict: true,
            trim: true,
          }),
          `hit_${hit._id}`,
        ].join('_');

        return {
          name,
          url: entry.url,
        };
      });

      const fileName = [
        format(parseISO(firstHit._created), LOCAL_DATETIME_FORMAT_FILENAME, {
          timeZone: LOCAL_TIMEZONE,
        }),
        slugify(hitterName, {
          replacement: '_',
          strict: true,
          trim: true,
        }),
      ].join('_');

      await FileHelper.zipFiles({
        fileName,
        files: files.filter((f) => !!f) as IZipFile[],
      });
    },
    [props.identifier, props.verbose, hitters]
  );

  const checkedActions = useCallback((checked: ISessionHit[]) => {
    const output: IMenuAction[] = [
      {
        prefixIcon: <DownloadIcon />,
        label: 'Download Selected Videos',
        onClick: async () => {
          const withVideos = checked.filter((h) =>
            getFireVideoID({
              annotated_video_id: h.fire.annotated_video_id,
              replay_video_id: h.fire.replay_video_id,
            })
          );

          if (withVideos.length === 0) {
            NotifyHelper.warning({
              message_md: 'There are no videos to download for your selection.',
            });
            return;
          }

          // check how many videos are checked
          if (withVideos.length >= MIN_CONFIRM_COUNT) {
            // show confirmation if "too many" are requested at once
            setDialogDownload({
              key: Date.now(),
              hits: withVideos,
            });
            return;
          }

          // otherwise proceed w/ download
          handleDownloadVideos(withVideos);
        },
      },
      {
        prefixIcon: <PersonIcon />,
        label: 'hitters.reassign-to-another-hitter',
        onClick: () =>
          setDialogReassign({
            key: Date.now(),
            hits: checked,
          }),
      },
    ];

    return output;
  }, []);

  return (
    <ErrorBoundary identifier="SessionHitsTable">
      <TableProvider identifier="SessionHitsTable" verbose={props.verbose}>
        <CheckedProvider data={filteredHits}>
          <CommonTable
            dataTestID="SessionHits"
            identifier="SessionHitsTable"
            displayColumns={columns}
            displayData={filteredHits}
            loading={loading}
            toolbarContent={!props.skipHeader && <SessionHitsToolbar />}
            checkboxColumnIndex={0}
            checkedActions={checkedActions}
            noDataHeader="No Results to Display"
            noDataBody="Fire a BP pitch to see hitting results appear here."
            noDataButton={
              props.backToPitches ? (
                <Box mt="2">
                  <Button onClick={props.backToPitches} loading={loading}>
                    Back to Pitches
                  </Button>
                </Box>
              ) : undefined
            }
            rowClassNameFn={(h: ISessionHit) => {
              if (!h) {
                return '';
              }

              if (h._id === updatedHitID) {
                return 'table-updated-row';
              }

              if (h._id === newestHitID) {
                return 'table-new-row';
              }

              return '';
            }}
            onRowClick={onOpenHit ? handleRowClick : undefined}
            onRowHover={props.onHoverHit}
            {...pagination}
            {...sort}
            vFlex={props.vFlex ?? true}
            disableScrolling={props.disableScrolling}
          />

          <SortedHitsProbe onChange={handleSortedChange} />

          {dialogPlayback && hitPlaybackID && (
            <SessionHitsVideoDialog
              identifier="HitterSessionShotVideo"
              title="common.shot-data"
              hitID={hitPlaybackID}
              hits={hitsWithVideos}
              onClose={() => {
                setDialogPlayback(undefined);
                setHitPlaybackID('');
              }}
              onPlaybackChange={setHitPlaybackID}
              openDetectionIssueDialog={(hit) =>
                setDialogDetectionIssue({
                  key: Date.now(),
                  hitID: hit._id,
                  detectionIssue: hit.detectionIssue,
                })
              }
            />
          )}
        </CheckedProvider>
      </TableProvider>

      {dialogDownload && (
        <CommonConfirmationDialog
          key={dialogDownload.key}
          identifier="ConfirmDownloadHitVideos"
          title="Large Download Detected"
          content={
            <p>
              You are about to download {dialogDownload.hits.length} videos.
              Preparing and transferring this many files can take an extended
              amount of time. Keep this tab open until your browser begins the
              downloads.
            </p>
          }
          action={{
            label: 'common.download',
            color: RADIX.COLOR.ACCENT,
            onClick: () => handleDownloadVideos(dialogDownload.hits),
          }}
        />
      )}

      {dialogReassign && (
        <CommonConfirmationDialog
          key={dialogReassign.key}
          identifier="ConfirmReassignHits"
          title="Reassign Hits"
          content={
            <CommonFormGrid columns={1}>
              <p>
                Please select a new hitter below to reassign{' '}
                {dialogReassign.hits.length} hit(s) to.
              </p>

              <CommonSearchInput
                id="reassign-hitter"
                placeholder="common.hitter"
                options={hitterOptions}
                values={reassignHitterID ? [reassignHitterID] : []}
                onChange={(v) => setReassignHitterID(first(v))}
              />
            </CommonFormGrid>
          }
          action={{
            label: 'common.proceed',
            color: RADIX.COLOR.WARNING,
            onClick: async () => {
              if (!reassignHitterID) {
                return;
              }

              if (!sessionID) {
                return;
              }

              const success = await HittersService.getInstance().updateHits({
                filter: {
                  session: sessionID,
                  hitterID: hitterID || null,
                  hitIDs: dialogReassign.hits.map((h) => h._id),
                },
                model: {
                  hitterID: reassignHitterID,
                },
              });

              if (success) {
                useAnalyticsStore.setState({ refetchKey: Date.now() });
              }
            },
          }}
        />
      )}

      {dialogDetectionIssue && (
        <DetectionIssueDialog
          key={dialogDetectionIssue.key}
          hitID={dialogDetectionIssue.hitID}
          defaultDetectionIssue={dialogDetectionIssue.detectionIssue}
          onSave={useAnalyticsStore.getState().reportDetectionIssue}
          onClose={() => setDialogDetectionIssue(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
